import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_LABELS } from "@/components/badges";
import { toast } from "sonner";
import { CalendarOff, CheckCircle2, Trash2 } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export function DelegationForm({ userId, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [current, setCurrent] = useState(null);
  const [active, setActive] = useState(false);
  const [subName, setSubName] = useState(null);
  const [targetName, setTargetName] = useState("");
  const [form, setForm] = useState({ substitute_id: "", start_date: todayISO(), end_date: todayISO(), reason: "" });

  const load = () => {
    setLoading(true);
    api.get(`/users/${userId}/delegation-candidates`)
      .then(({ data }) => {
        setCandidates(data.candidates || []);
        setCurrent(data.delegation || null);
        setActive(!!data.delegation_active);
        setSubName(data.substitute_name || null);
        setTargetName(data.target?.name || "");
        if (data.delegation) {
          setForm({
            substitute_id: data.delegation.substitute_id || "",
            start_date: data.delegation.start_date || todayISO(),
            end_date: data.delegation.end_date || todayISO(),
            reason: data.delegation.reason || "",
          });
        }
      })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (userId) load(); }, [userId]);

  const canSave = form.substitute_id && form.start_date && form.end_date && form.end_date >= form.start_date;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/users/${userId}/delegation`, {
        substitute_id: form.substitute_id,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || null,
      });
      setCurrent(data.delegation);
      setActive(!!data.delegation_active);
      setSubName(data.substitute_name);
      toast.success("Délégation d'absence enregistrée");
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/users/${userId}/delegation`);
      setCurrent(null);
      setActive(false);
      setSubName(null);
      setForm({ substitute_id: "", start_date: todayISO(), end_date: todayISO(), reason: "" });
      toast.success("Délégation retirée");
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-6 text-center text-sm text-muted-foreground" data-testid="delegation-loading">Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="delegation-form">
      {current && (
        <div className={active
          ? "flex items-start gap-2 rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          : "flex items-start gap-2 rounded-sm border border-border bg-muted/40 p-3 text-sm"}
          data-testid="delegation-current">
          {active ? <CalendarOff className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
          <div>
            {active
              ? <><span className="font-semibold">Absence en cours</span> — remplacé(e) par <span className="font-semibold">{subName}</span> jusqu'au {current.end_date}.</>
              : <><span className="font-semibold">Absence programmée</span> — {subName} du {current.start_date} au {current.end_date}.</>}
            {current.reason && <div className="mt-0.5 text-xs opacity-80">Motif : {current.reason}</div>}
          </div>
        </div>
      )}

      {candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun collègue actif disponible comme remplaçant pour {targetName}.</p>
      ) : (
        <>
          <div className="space-y-2">
            <Label>Remplaçant *</Label>
            <Select value={form.substitute_id} onValueChange={(v) => setForm({ ...form, substitute_id: v })}>
              <SelectTrigger data-testid="delegation-substitute-select" className="rounded-sm"><SelectValue placeholder="Choisir un collègue…" /></SelectTrigger>
              <SelectContent>
                {candidates.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`delegation-candidate-${c.id}`}>
                    {c.name} — {ROLE_LABELS[c.role] || c.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date de début *</Label>
              <Input data-testid="delegation-start-date" type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Date de fin *</Label>
              <Input data-testid="delegation-end-date" type="date" value={form.end_date} min={form.start_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Motif de l'absence</Label>
            <Textarea data-testid="delegation-reason" rows={2} value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Congés, arrêt maladie, formation…" className="rounded-sm text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={save} disabled={saving || !canSave}
              data-testid="delegation-save-button" className="rounded-sm bg-primary text-white hover:bg-red-700">
              {saving ? "Enregistrement…" : current ? "Mettre à jour" : "Activer la délégation"}
            </Button>
            {current && (
              <Button type="button" variant="outline" onClick={remove} disabled={saving}
                data-testid="delegation-remove-button" className="rounded-sm text-primary">
                <Trash2 className="mr-1.5 h-4 w-4" /> Retirer
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
